import React, { useContext, useEffect, useState } from "react"
import { Button } from "../shared/Button"
import { LoadingButton } from "../shared/LoadingButton"
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
} from "@mui/material"
import dayjs from "dayjs"
import "./EventFormStyles.css"
import "cropperjs/dist/cropper.css"
import LocationSearchInput from "../shared/fields/LocationSearchInput"
import { UserContext } from "../HomeApp"
import TaggableInput from "../shared/fields/TaggableInput"
import { useUserTags } from "../../hooks/useUserTags"
import PostDeleteButton from "../PostDeleteButton"
import { EventCancelModal } from "./EventCancelModal"
import { useQueryClient } from "react-query"
import { useUserBandsCountFor } from "../../hooks/useUserBandsCountFor"
import { useUserSceneCount } from "../../hooks/useUserSceneCount"
import { EventFlyerPhotoUploadSection } from "./EventFlyerPhotoUploadSection"
import { NotifyAttendeesCheckbox } from "./NotifyAttendeesCheckbox"
import {
  createEvent,
  updateEvent,
  uploadEventImages,
  cancelEventPost,
} from "../../util/backend_api/posts"
import { toast } from "react-toastify"
import { root_path, for_venues_path } from "../../routes"
import {
  formatPrice,
  calculateBandShareForTicketPrice,
  calculateGrossPriceForBandShare,
} from "../../util/ticketUtils"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useBandPosts } from "../../hooks/useBandPosts"
import { useDuplicateEventCheck } from "../../hooks/useDuplicateEventCheck"
import { getBandPost, type BandPostResponse } from "../../requests/bands"
import { DuplicateEventWarning } from "./DuplicateEventWarning"
import { Spinner } from "../shared/Spinner"
import { InfoTooltip } from "../shared/InfoTooltip"
import { EventPerformance } from "./EventPerformance"
import EventPerformanceForm from "./EventPerformanceForm"
import CollaboratorsForm from "./CollaboratorsForm"
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd"
// Import types and constants
import {
  EventPerformanceType,
  CollaboratorEntry,
  EventFormProps,
  EventFormData,
  EventPostData,
  EventResponse,
  TicketPricingScheduleEntry,
} from "../../types/events"
import {
  EVENT_DURATION_DEFAULT,
  eventDurationOptions,
  TICKET_TYPES,
  ticketTypeOptions,
  PRICING_TYPES,
  pricingTypeOptionsFor,
  FEE_TYPES,
  eventStatusOptions,
  defaultConcertProps,
  extractDateFromStartDt,
} from "../../constants/events"
import { eventValidationSchema } from "../../validation/eventForm"
import { useRollbar } from "@rollbar/react"
import { Trash } from "react-bootstrap-icons"

// Consistent styling for all MUI inputs
const muiInputStyles = {
  width: "100%",
}

const EventForm: React.FC<EventFormProps> = ({
  setShowForm = null,
  concert = defaultConcertProps,
  setConcert = null,
}) => {
  const { user } = useContext(UserContext)
  const queryClient = useQueryClient()
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false)
  const [userTags, setUserTags] = useState<any[]>([])
  const [formError, setFormError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [cropper, setCropper] = useState<any>(null)
  const [originalFile, setOriginalFile] = useState<File | null>(null)
  // True while a selected flyer photo is still being converted/cropped
  // (e.g. HEIC->JPEG) - submit is blocked until this clears, otherwise the
  // form can submit before the image is ready and the event gets created
  // with no cover image at all.
  const [isProcessingPhoto, setIsProcessingPhoto] = useState<boolean>(false)
  const [removeCoverImage, setRemoveCoverImage] = useState<boolean>(false)
  const [isGeneratedCover, setIsGeneratedCover] = useState<boolean>(
    !!(concert as any).content_hash?.cover_image_generated,
  )
  const [gifPreview, setGifPreview] = useState<string | null>(null)
  const [eventPerformances, setEventPerformances] = useState<
    EventPerformanceType[]
  >(concert.event_performances || [])
  const [currentLineupIndex, setCurrentLineupIndex] = useState(0)
  const [editingPerformance, setEditingPerformance] =
    useState<EventPerformanceType | null>(null)
  const [collaborators, setCollaborators] = useState<CollaboratorEntry[]>(
    concert.post_collaborators || [],
  )
  const [sendNotifications, setSendNotifications] = useState<boolean>(
    !concert.id, // true for new events, false for editing existing events
  )
  const [pricingScheduleIdCounter, setPricingScheduleIdCounter] = useState(0)
  const [showCancelModal, setShowCancelModal] = useState(false)
  // When editing an event that already has a stored end time, show it as-is
  // instead of making the user re-derive it from Duration.
  const [useExplicitEndTime, setUseExplicitEndTime] = useState(!!concert.end_dt)
  // Duration starts as the friendly preset dropdown. Once the user derives a
  // duration from an explicit end time (or we're editing an event whose
  // duration was itself derived from a stored end time), it likely won't
  // match a preset anymore, so it's shown as a plain minutes input instead.
  const [durationIsCustom, setDurationIsCustom] = useState(!!concert.end_dt)
  const [pricingSchedule, setPricingSchedule] = useState<
    TicketPricingScheduleEntry[]
  >(() => {
    const raw = (concert as any).ticket_pricing_schedule
    if (!Array.isArray(raw) || raw.length === 0) return []
    return raw.map((row: any, i: number) => ({
      id: i,
      price_changes_at:
        typeof row.price_changes_at === "string"
          ? row.price_changes_at
          : row.price_changes_at != null
            ? new Date(row.price_changes_at).toISOString()
            : "",
      // price_changes_to_entered (when present) is what the band actually
      // typed; price_changes_to is the grossed-up buyer-facing price - show
      // the band their own number back, same as bandNadaTicketPrice above.
      price_changes_to:
        typeof row.price_changes_to_entered === "number"
          ? row.price_changes_to_entered
          : typeof row.price_changes_to === "number"
            ? row.price_changes_to
            : 0,
    }))
  })
  const rollbar = useRollbar()
  const isEdit = !!concert.id

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(eventValidationSchema),
    context: { isEdit },
    defaultValues: {
      title: concert.title || "",
      startDate: extractDateFromStartDt(concert.start_dt),
      durationMinutes: concert.duration_minutes || EVENT_DURATION_DEFAULT.value,
      endsAt: concert.end_dt ? new Date(concert.end_dt) : null,
      ticketType: concert.ticket_type || TICKET_TYPES.NO_ADVANCE_TICKETS.value,
      eventLocation: concert.location || { displayName: "" },
      eventTicketLinkUrl: concert.ticket_link_url || "",
      eventDescription: concert.description || "",
      coverImageUrl: concert.cover_image_url || null,
      eventStatus: concert?.id
        ? (concert as any).status || "published"
        : "published",
      maxRsvps: concert.max_rsvps || null,
      // When fee_type is pre_purchase, ticket_price/_max hold the grossed-up
      // buyer-facing price; ticket_price_entered/_max_entered (when present)
      // hold what the band actually typed, and are what the form should
      // show back to them on re-edit.
      bandNadaTicketPrice:
        (concert as any).ticket_price_entered ?? (concert as any).ticket_price ?? null,
      bandNadaTicketPriceMax:
        (concert as any).ticket_price_max_entered ?? (concert as any).ticket_price_max ?? null,
      bandNadaTicketMax: (concert as any).band_nada_ticket_max || null,
      ticketPriceAtDoor: (concert as any).ticket_price_at_door || null,
      ticketPrice: (concert as any).ticket_price || null,
      ticketPriceMax: (concert as any).ticket_price_max || null,
      feeType: (concert as any).fee_type || FEE_TYPES.POST_PURCHASE.value,
      pricingType:
        (concert as any).pricing_type ||
        ((concert as any).ticket_type === TICKET_TYPES.BN_TICKETS.value
          ? PRICING_TYPES.SET_PRICE.value
          : PRICING_TYPES.FREE.value),
      acceptsDonation: (concert as any).accepts_donation || false,
      donationText: (concert as any).donation_text || "",
      timezone:
        (concert as any).timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      announcedAt: (concert as any).announced_at
        ? new Date((concert as any).announced_at)
        : null,
    },
  })
  const currentTicketType = watch("ticketType")
  const watchedStartDate = watch("startDate")
  const watchedEndsAt = watch("endsAt")

  // Both the Duration field and the Ends At field stay mounted at all times
  // (see the render below), so it's safe to set either one directly in these
  // click handlers — no mount-timing race to work around anymore.
  const switchToExplicitEndTime = () => {
    if (!watchedEndsAt) {
      const base = watchedStartDate ? dayjs(watchedStartDate) : dayjs()
      setValue(
        "endsAt",
        base.add(EVENT_DURATION_DEFAULT.value, "minute").toDate(),
      )
    }
    setUseExplicitEndTime(true)
  }

  const switchToDuration = () => {
    if (watchedEndsAt && watchedStartDate) {
      const minutes = Math.max(
        1,
        dayjs(watchedEndsAt).diff(dayjs(watchedStartDate), "minute"),
      )
      setValue("durationMinutes", minutes)
      setDurationIsCustom(true)
    }
    setUseExplicitEndTime(false)
  }

  const bandId = isEdit ? concert.band_id : user?.band?.id
  const { data: pastConcertsData, isLoading: isLoadingPastEvents } =
    useBandPosts(bandId ?? 0, "concerts", true, 1, 20)

  // When editing, fetch full post with content_hash so pricing schedule is available (list API often omits it)
  const concertId = concert?.id
  const concertBandId = concert?.band_id
  useEffect(() => {
    if (!concertId || !concertBandId) return
    let cancelled = false
    getBandPost(concertBandId, concertId, { fields: "content_hash" })
      .then((response) => {
        if (cancelled || !response?.post) return
        const ch = response.post.content_hash as any
        setIsGeneratedCover(!!ch?.cover_image_generated)

        const raw = ch?.ticket_pricing_schedule
        if (Array.isArray(raw) && raw.length > 0) {
          setPricingSchedule(
            raw.map((row: any, i: number) => ({
              id: i,
              price_changes_at:
                typeof row.price_changes_at === "string"
                  ? row.price_changes_at
                  : row.price_changes_at != null
                    ? new Date(row.price_changes_at).toISOString()
                    : "",
              // Prefer what the band actually typed (price_changes_to_entered)
              // over the grossed-up buyer-facing price, same as the initial
              // pricingSchedule state above - this fetch runs right after
              // mount and would otherwise clobber that with the grossed value.
              price_changes_to:
                typeof row.price_changes_to_entered === "number"
                  ? row.price_changes_to_entered
                  : typeof row.price_changes_to === "number"
                    ? row.price_changes_to
                    : 0,
            })),
          )
          setPricingScheduleIdCounter(raw.length)
        }

        // Repopulate pricing fields from the authoritative content_hash
        const isBnTickets = ch?.ticket_type === TICKET_TYPES.BN_TICKETS.value
        setValue(
          "pricingType",
          ch?.pricing_type ??
            (isBnTickets
              ? PRICING_TYPES.SET_PRICE.value
              : PRICING_TYPES.FREE.value),
        )
        setValue("feeType", ch?.fee_type ?? FEE_TYPES.POST_PURCHASE.value)
        setValue("ticketPrice", ch?.ticket_price ?? null)
        setValue("ticketPriceMax", ch?.ticket_price_max ?? null)
        setValue(
          "bandNadaTicketPrice",
          ch?.ticket_price_entered ?? ch?.ticket_price ?? null,
        )
        setValue(
          "bandNadaTicketPriceMax",
          ch?.ticket_price_max_entered ?? ch?.ticket_price_max ?? null,
        )
        setValue("acceptsDonation", ch?.accepts_donation ?? false)
        setValue("donationText", ch?.donation_text ?? "")
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [concertId, concertBandId])
  const formatEventDate = (dateStr: string | Date | null | undefined) => {
    if (!dateStr) return ""
    const d = dayjs(dateStr)
    return d.isValid() ? d.format("MM/DD/YYYY") : ""
  }
  const pastEvents = React.useMemo(() => {
    const posts = (pastConcertsData?.posts ?? []).filter(
      (p: any) => p.post_type !== "external_event",
    )
    return [...posts].sort((a: any, b: any) => {
      const dateA = a.active_date || a.start_dt || a.published_at || ""
      const dateB = b.active_date || b.start_dt || b.published_at || ""
      return new Date(dateB).getTime() - new Date(dateA).getTime()
    })
  }, [pastConcertsData])
  const [selectedDuplicateEventId, setSelectedDuplicateEventId] = useState<
    string | number
  >("")

  const handleDuplicateFromPastEvent = async (
    eventId: string | number,
  ): Promise<void> => {
    if (!eventId || !bandId) return
    try {
      const response: BandPostResponse = await getBandPost(bandId, eventId, {
        fields: "content_hash",
      })
      const post = response?.post
      if (!post) return
      setValue("title", post.title ?? "")
      // Leave startDate unchanged (do not copy from past event)
      setValue(
        "durationMinutes",
        post.duration_minutes ?? EVENT_DURATION_DEFAULT.value,
      )
      setValue(
        "ticketType",
        post.ticket_type ??
          post.content_hash?.ticket_type ??
          TICKET_TYPES.NO_ADVANCE_TICKETS.value,
      )
      setValue("eventLocation", {
        ...(post.location ?? {}),
        displayName:
          (post.location as any)?.displayName ??
          (post.location as any)?.formattedAddress ??
          "",
      })
      setValue("eventTicketLinkUrl", post.ticket_link_url ?? "")
      setValue("eventDescription", post.description ?? "")
      setValue("coverImageUrl", post.cover_image_url ?? null)
      setValue("eventStatus", post.status ?? "published")
      setValue("maxRsvps", post.max_rsvps ?? null)
      setValue(
        "feeType",
        (post as any).fee_type ??
          (post.content_hash as any)?.fee_type ??
          FEE_TYPES.POST_PURCHASE.value,
      )
      setValue(
        "bandNadaTicketPrice",
        (post as any).ticket_price_entered ??
          (post.content_hash as any)?.ticket_price_entered ??
          post.ticket_price ??
          post.content_hash?.ticket_price ??
          null,
      )
      setValue(
        "bandNadaTicketPriceMax",
        (post as any).ticket_price_max_entered ??
          (post.content_hash as any)?.ticket_price_max_entered ??
          (post as any).ticket_price_max ??
          (post.content_hash as any)?.ticket_price_max ??
          null,
      )
      setValue(
        "bandNadaTicketMax",
        post.band_nada_ticket_max ??
          post.content_hash?.band_nada_ticket_max ??
          null,
      )
      setValue(
        "ticketPriceAtDoor",
        post.ticket_price_at_door ??
          post.content_hash?.ticket_price_at_door ??
          null,
      )
      setValue(
        "ticketPrice",
        post.ticket_price ?? (post.content_hash as any)?.ticket_price ?? null,
      )
      setValue(
        "ticketPriceMax",
        post.ticket_price_max ??
          (post.content_hash as any)?.ticket_price_max ??
          null,
      )
      setValue(
        "pricingType",
        post.pricing_type ??
          (post.content_hash as any)?.pricing_type ??
          PRICING_TYPES.FREE.value,
      )
      setValue(
        "acceptsDonation",
        (post as any).accepts_donation ??
          (post.content_hash as any)?.accepts_donation ??
          false,
      )
      setValue(
        "donationText",
        (post as any).donation_text ??
          (post.content_hash as any)?.donation_text ??
          "",
      )
      const rawSchedule = (post.content_hash as any)?.ticket_pricing_schedule
      if (Array.isArray(rawSchedule) && rawSchedule.length > 0) {
        setPricingSchedule(
          rawSchedule.map((row: any, i: number) => ({
            id: i,
            price_changes_at:
              typeof row.price_changes_at === "string"
                ? row.price_changes_at
                : row.price_changes_at != null
                  ? new Date(row.price_changes_at).toISOString()
                  : "",
            price_changes_to:
              typeof row.price_changes_to_entered === "number"
                ? row.price_changes_to_entered
                : typeof row.price_changes_to === "number"
                  ? row.price_changes_to
                  : 0,
          })),
        )
        setPricingScheduleIdCounter(rawSchedule.length)
      }
      const performances: EventPerformanceType[] = (
        post.event_performances ?? []
      ).map((p: any, index: number) => ({
        performance_order: index + 1,
        set_time: p.set_time ?? null,
        band_id: p.band_id ?? null,
        band_name: p.band_name ?? p.pending_band_name ?? "",
        is_existing_band: p.is_existing_band ?? !!p.band_id,
        search_query: p.band_name ?? p.pending_band_name ?? "",
        search_results: [],
        show_email_field: false,
        is_added: true,
        pending_band_name: p.pending_band_name ?? undefined,
        pending_band_email: p.pending_band_email ?? undefined,
      }))
      setEventPerformances(performances)
      setSelectedDuplicateEventId(eventId)
    } catch (err) {
      console.error("Failed to load past event for duplicate:", err)
      toast.error("Could not load that event. Please try again.")
    }
  }

  // Extract bandId from eventLocation when it changes
  const eventLocation = watch("eventLocation")
  const [eventLocationBandId, setEventLocationBandId] = useState<number | null>(
    null,
  )

  useEffect(() => {
    const location = eventLocation as any
    // Check for both bandId and venueId (venues use venueId which is the band ID)
    const bandId = location?.bandId || location?.venueId || null
    setEventLocationBandId(bandId)
  }, [eventLocation])

  // Duplicate-event warning: only once the user has picked both a date and a
  // location for a brand-new event (not while editing an existing one).
  const [debouncedStartDate, setDebouncedStartDate] = useState(watchedStartDate)
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedStartDate(watchedStartDate)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [watchedStartDate])

  const eventLocationDisplayName = (eventLocation as any)?.displayName
  const [dismissedDuplicateId, setDismissedDuplicateId] = useState<
    number | null
  >(null)
  const { duplicate: possibleDuplicate } = useDuplicateEventCheck({
    bandId: bandId ?? null,
    startDatetime: debouncedStartDate
      ? dayjs(debouncedStartDate).toISOString()
      : null,
    venueBandId: eventLocationBandId,
    enabled: !isEdit && !!bandId && !!eventLocationDisplayName,
  })

  // Calculate band IDs for follower count: user's band + all band_ids from eventPerformances
  const userBandId = user?.band?.id
  const bandIdsForCount = React.useMemo(() => {
    const bandIds: number[] = []

    // Add the logged-in user's band ID
    if (userBandId) {
      bandIds.push(userBandId)
    }

    // Add all band_ids from eventPerformances (excluding nulls and destroyed items)
    eventPerformances
      .filter((perf) => !perf._destroy && perf.band_id !== null)
      .forEach((perf) => {
        if (perf.band_id && !bandIds.includes(perf.band_id)) {
          bandIds.push(perf.band_id)
        }
      })

    // Add the band ID from the event location if present
    if (eventLocationBandId && !bandIds.includes(eventLocationBandId)) {
      bandIds.push(eventLocationBandId)
    }

    return bandIds.length > 0 ? bandIds : null
  }, [userBandId, eventPerformances, eventLocationBandId])

  const collaboratorSuggestions = React.useMemo(() => {
    const seen = new Set<number>()
    const result: { band_id: number; band_name: string }[] = []
    eventPerformances.forEach((p) => {
      if (p.band_id && !p._destroy && !seen.has(p.band_id)) {
        seen.add(p.band_id)
        result.push({ band_id: p.band_id, band_name: p.band_name })
      }
    })
    if (eventLocationBandId && !seen.has(eventLocationBandId)) {
      const displayName = (eventLocation as any)?.displayName || "Venue"
      result.push({ band_id: eventLocationBandId, band_name: displayName })
    }
    return result
  }, [eventPerformances, eventLocationBandId, eventLocation])

  // Get follower count
  const { data: followerCounts, isLoading: isLoadingFollowerCount } =
    useUserBandsCountFor(bandIdsForCount)

  const notifiersCount = followerCounts?.notifiersCount

  // Reach for an event is scene-wide (everyone in the acting band's scene
  // sees it in their feed/digest), not limited to the band's own
  // followers — this drives the "will see it regardless" count below
  // rather than followerCount. Shared with mobile via the same endpoint.
  const scene = user?.band?.scene_list
  const { data: sceneCountData, isLoading: isLoadingSceneCount } =
    useUserSceneCount(scene)
  const sceneCount = sceneCountData?.count
  // There's only ever been one real scene ("new york city") so nothing
  // else in this app already title-cases one for display — the header
  // just hardcodes "New York City" — but deriving it here instead keeps
  // this correct if a second scene is ever added.
  const sceneLabel = scene
    ? scene.replace(/\b\w/g, (char) => char.toUpperCase())
    : null

  // Clear maxRsvps when switching to bandNada tickets
  useEffect(() => {
    if (currentTicketType === TICKET_TYPES.BN_TICKETS.value) {
      setValue("maxRsvps", null)
    }
  }, [currentTicketType, setValue])

  // Clear form error when user starts typing
  useEffect(() => {
    const subscription = watch(() => {
      if (formError) {
        setFormError(null)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, formError])

  const displayNewEventPost = (
    response: Promise<EventResponse> | EventResponse,
  ) => {
    const handleResponse = (data: EventResponse) => {
      if (data.success) {
        if (isEdit) {
          setConcert(data.post!)
          setCollaborators(data.post?.post_collaborators || [])
          setIsUploading(false)
        } else {
          toast.success(
            "Thanks for posting your event! (sending you there in...)",
            {
              autoClose: 2000,
              onClose: () => {
                setIsUploading(false)
                window.location.href = data.post!.nada_url!
              },
            },
          )
        }
      } else {
        setIsUploading(false)
        // Handle validation errors from backend
        if (data.errors && data.errors.error) {
          const errorMessages = Array.isArray(data.errors.error)
            ? data.errors.error
            : [data.errors.error]
          setFormError(errorMessages.join(". "))
        } else {
          setFormError(
            "Failed to create event. Please check your input and try again.",
          )
        }
      }
    }

    if (response instanceof Promise) {
      response.then(handleResponse).catch((error) => {
        setIsUploading(false)
        setFormError("An unexpected error occurred. Please try again.")
        console.error("Error displaying new event post:: ", error)
        rollbar.error("Error displaying new event post: ", error)
      })
    } else {
      handleResponse(response)
    }
  }

  const postHash = (
    formValues: EventFormData,
    croppedSignedId?: string,
    originalSignedId?: string,
  ): EventPostData => {
    const processedPerformances = eventPerformances?.map(
      (performance, index) => {
        if (performance._destroy) {
          // For deleted performances, just add _destroy flag
          return { ...performance, _destroy: true }
        } else if (performance.id && performance.band_id) {
          // Existing band performance
          return { ...performance }
        } else if (performance.id) {
          // Existing pending performance - sync pending_band_name from band_name in case it was edited
          return { ...performance, pending_band_name: performance.band_name }
        } else {
          // New performance - add performance_order
          return {
            ...performance,
            pending_band_name: performance.band_name,
            performance_order: index + 1,
          }
        }
      },
    )

    const resolvedDurationMinutes =
      useExplicitEndTime && formValues.endsAt && formValues.startDate
        ? Math.max(
            1,
            dayjs(formValues.endsAt).diff(
              dayjs(formValues.startDate),
              "minute",
            ),
          )
        : formValues.durationMinutes

    let postValues: EventPostData = {
      post_type: "event",
      title: formValues.title,
      published_at: formValues.startDate,
      announced_at:
        formValues.eventStatus !== "unpublished" && formValues.announcedAt
          ? formValues.announcedAt.toISOString()
          : null,
      status: formValues.eventStatus,
      timezone: formValues.timezone,
      event_performances_attributes: processedPerformances,
      content_hash: {
        duration_minutes: resolvedDurationMinutes,
        ticket_type: formValues.ticketType,
        location: formValues.eventLocation,
        ticket_link_url: formValues.eventTicketLinkUrl,
        description: formValues.eventDescription,
        start: {
          datetime: formValues.startDate,
        },
      },
    }

    // Add max_rsvps only for non-bandNada ticket events
    if (formValues.ticketType !== TICKET_TYPES.BN_TICKETS.value) {
      postValues.content_hash.max_rsvps = formValues.maxRsvps || null
    }

    // Add external ticket pricing for FREE (RSVP Only) and EXTERNAL_TICKETS
    if (
      formValues.ticketType === TICKET_TYPES.NO_ADVANCE_TICKETS.value ||
      formValues.ticketType === TICKET_TYPES.EXTERNAL_TICKETS.value
    ) {
      postValues.content_hash.pricing_type = formValues.pricingType
      postValues.content_hash.ticket_price =
        formValues.pricingType === PRICING_TYPES.FREE.value
          ? null
          : formValues.ticketPrice
      postValues.content_hash.ticket_price_max =
        formValues.pricingType === PRICING_TYPES.SLIDING_SCALE.value
          ? formValues.ticketPriceMax
          : null
    }

    // Add BandNada ticket data when ticket type is BN_TICKETS
    if (formValues.ticketType === TICKET_TYPES.BN_TICKETS.value) {
      const bnPricingType = formValues.pricingType
      const isFree = bnPricingType === PRICING_TYPES.FREE.value
      const isPrePurchase =
        !isFree && formValues.feeType === FEE_TYPES.PRE_PURCHASE.value
      postValues.content_hash.pricing_type = bnPricingType
      postValues.content_hash.fee_type = isFree
        ? FEE_TYPES.POST_PURCHASE.value
        : formValues.feeType || FEE_TYPES.POST_PURCHASE.value
      // ticket_price is always sent as a valid positive number (required by
      // backend validation, which runs before the gross-up); when
      // pre_purchase, the backend then derives the real buyer-facing price
      // from ticket_price_entered below and overwrites this value - what we
      // send here doesn't matter beyond passing that validation.
      postValues.content_hash.ticket_price = isFree
        ? 0
        : formValues.bandNadaTicketPrice
      postValues.content_hash.ticket_price_entered = isPrePurchase
        ? formValues.bandNadaTicketPrice
        : null
      postValues.content_hash.ticket_price_max =
        bnPricingType === PRICING_TYPES.SLIDING_SCALE.value
          ? formValues.bandNadaTicketPriceMax
          : null
      postValues.content_hash.ticket_price_max_entered =
        isPrePurchase && bnPricingType === PRICING_TYPES.SLIDING_SCALE.value
          ? formValues.bandNadaTicketPriceMax
          : null
      postValues.content_hash.band_nada_ticket_max =
        formValues.bandNadaTicketMax
      postValues.content_hash.ticket_price_at_door = isFree
        ? 0
        : formValues.ticketPriceAtDoor
      if (pricingSchedule.length > 0) {
        postValues.content_hash.ticket_pricing_schedule = pricingSchedule.map(
          ({ price_changes_at, price_changes_to }) => ({
            price_changes_at: price_changes_at || "",
            price_changes_to,
            price_changes_to_entered: isPrePurchase ? price_changes_to : null,
          }),
        )
      }
    }

    // Donations only apply to Free events — only send accepts_donation when
    // pricingType is Free, so a stale checked value from a prior pricing
    // type can never sneak through.
    const isFreePricing = formValues.pricingType === PRICING_TYPES.FREE.value
    postValues.content_hash.accepts_donation =
      isFreePricing && !!formValues.acceptsDonation
    postValues.content_hash.donation_text =
      isFreePricing && formValues.acceptsDonation
        ? formValues.donationText || null
        : null

    if (croppedSignedId) {
      postValues.custom_cover_image = croppedSignedId
      postValues.attached_media = [originalSignedId!]
      postValues.content_hash.is_cropped = true
    } else if (originalSignedId) {
      postValues.custom_cover_image = originalSignedId
      postValues.attached_media = [originalSignedId]
      postValues.content_hash.is_cropped = false
    } else if (removeCoverImage) {
      postValues.remove_cover_image = true
    }

    const collaboratorAttrs = collaborators
      .filter((c) => c.id || !c._destroy)
      .map((c) => ({
        id: c.id,
        band_id: c.band_id,
        pending_band_email: c.pending_band_email,
        _destroy: c._destroy,
      }))
    if (collaboratorAttrs.length > 0) {
      postValues.post_collaborators_attributes = collaboratorAttrs
    }

    return postValues
  }

  const { isLoading, isError, data, error } = useUserTags(concert.id)

  useEffect(() => {
    if (!isLoading && !isError && data?.user_tags) {
      setUserTags(data.user_tags)
    } else if (isError) {
      console.error("ERROR: ", error)
    }
  }, [isLoading, isError, data, error])

  const createOrUpdateEvent = async (
    bandId: number,
    croppedBlob: Blob | null,
    formData: EventFormData,
  ) => {
    setIsUploading(true)
    setFormError(null) // Clear any previous errors

    try {
      const uploadImageResponse =
        croppedBlob || originalFile
          ? await uploadEventImages(originalFile, croppedBlob)
          : {}

      if (uploadImageResponse.error) {
        // The generic message we show the user collapses three very
        // different failure points (see uploadEventImages) into one string -
        // report the specifics here so a recurrence is diagnosable from
        // Rollbar alone instead of reconstructing it from Heroku/Papertrail.
        rollbar.warning("Event cover image upload failed", {
          uploadStage: uploadImageResponse.uploadStage,
          rawError: uploadImageResponse.rawError,
          fileInfo: uploadImageResponse.fileInfo,
          bandId,
          isEdit,
          postId: concert?.id ?? null,
        })
        setFormError(uploadImageResponse.error)
        setIsUploading(false)
        return
      }

      const postData = postHash(
        formData,
        uploadImageResponse.croppedSignedId,
        uploadImageResponse.originalSignedId,
      )

      let responseData = isEdit
        ? await updateEvent(
            bandId,
            concert.id!,
            postData,
            userTags,
            sendNotifications,
          )
        : await createEvent(
            bandId,
            postData,
            userTags,
            sendNotifications,
            selectedDuplicateEventId || undefined,
          )

      if (responseData) {
        setRemoveCoverImage(false)
        displayNewEventPost(responseData)
        setTimeout(() => {
          queryClient.invalidateQueries("user-posts")
        }, 500)
      } else {
        setFormError("Failed to create event. Please try again.")
        setIsUploading(false)
      }
    } catch (error) {
      setFormError("An unexpected error occurred. Please try again.")
      setIsUploading(false)
      console.error("Error in createOrUpdateEvent: ", error)
      rollbar.error("Error creating/updating event: ", error)
    }
  }

  const onSubmit = async (data: EventFormData) => {
    if (isProcessingPhoto) {
      setFormError(
        "Your flyer photo is still processing - please wait a moment and try again.",
      )
      return
    }
    setIsUploading(true)
    try {
      if (cropper !== null) {
        await new Promise<void>((resolve, reject) => {
          cropper.getCroppedCanvas().toBlob(async (blob) => {
            try {
              await createOrUpdateEvent(user.band.id, blob, data)
              resolve()
            } catch (err) {
              reject(err)
            }
          })
        })
      } else {
        await createOrUpdateEvent(user.band.id, null, data)
      }

      if (isEdit) {
        setShowForm?.(false)
        queryClient.invalidateQueries(["posts"])
      }
    } catch (error: any) {
      setFormError(error.message)
      setIsUploading(false)
    }
  }

  const formFieldContainerClasses = "mb-2"

  const handleGifUpload = (gifFile: File | null) => {
    if (gifFile === null) {
      setGifPreview(null)
    } else {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (typeof e.target?.result === "string") {
          setGifPreview(e.target.result)
        }
      }
      reader.readAsDataURL(gifFile)
    }
  }

  // Helper function to sort performances by set time
  const sortPerformancesBySetTime = (
    performances: EventPerformanceType[],
  ): EventPerformanceType[] => {
    const getTimeValue = (time: Date | string | null): number => {
      if (!time) return 0
      if (time instanceof Date) return time.getTime()
      try {
        const date = new Date(time)
        return isNaN(date.getTime()) ? 0 : date.getTime()
      } catch (e) {
        return 0
      }
    }

    return [...performances]
      .sort((a, b) => {
        if (!a.set_time && !b.set_time) return 0
        if (!a.set_time) return 1
        if (!b.set_time) return -1
        return getTimeValue(a.set_time) - getTimeValue(b.set_time)
      })
      .map((perf, index) => ({ ...perf, performance_order: index + 1 }))
  }

  const applySort = (
    performances: EventPerformanceType[],
  ): EventPerformanceType[] => {
    const hasSetTimes = performances.some((p) => !p._destroy && p.set_time)
    return hasSetTimes ? sortPerformancesBySetTime(performances) : performances
  }

  const matchPerformance = (
    p: EventPerformanceType,
    target: EventPerformanceType,
  ): boolean => {
    if (p.id && target.id) return p.id === target.id
    return p.performance_order === target.performance_order
  }

  const addBandToLineup = (performance: EventPerformanceType) => {
    const newPerformances = [
      ...eventPerformances,
      { ...performance, performance_order: eventPerformances.length + 1 },
    ]
    setEventPerformances(applySort(newPerformances))
    setCurrentLineupIndex((prev) => prev + 1)
    queryClient.invalidateQueries(["user_bands_count"])
  }

  const removeEventPerformance = (performanceOrder: number) => {
    setEventPerformances((prev) =>
      prev.map((p) =>
        p.performance_order === performanceOrder ? { ...p, _destroy: true } : p,
      ),
    )
    setCurrentLineupIndex((prev) => prev - 1)
    queryClient.invalidateQueries(["user_bands_count"])
  }

  const editEventPerformance = (performance: EventPerformanceType) => {
    if (
      editingPerformance &&
      matchPerformance(editingPerformance, performance)
    ) {
      setEditingPerformance(null)
    } else {
      setEditingPerformance(performance)
    }
  }

  const updateEventPerformance = (updatedPerformance: EventPerformanceType) => {
    const updatedPerformances = eventPerformances.map((p) =>
      matchPerformance(p, updatedPerformance) ? updatedPerformance : p,
    )
    setEventPerformances(applySort(updatedPerformances))
    setEditingPerformance(null)
    queryClient.invalidateQueries(["user_bands_count"])
  }

  const cancelEdit = () => {
    setEditingPerformance(null)
  }

  const reorderPerformances = (result: DropResult) => {
    if (!result.destination) return
    const active = eventPerformances.filter((p) => !p._destroy)
    const [moved] = active.splice(result.source.index, 1)
    active.splice(result.destination.index, 0, moved)
    const reordered = active.map((p, i) => ({ ...p, performance_order: i + 1 }))
    const destroyed = eventPerformances.filter((p) => p._destroy)
    setEventPerformances([...reordered, ...destroyed])
  }

  const addCollaborator = (band: {
    id: number | null
    name: string
    cover_image_url: string | null
    email?: string
  }) => {
    setCollaborators((prev) => [
      ...prev,
      {
        band_id: band.id,
        band_name: band.name,
        cover_image_url: band.cover_image_url,
        status: "pending",
        pending_band_email: band.email,
      },
    ])
  }

  const removeCollaborator = (collab: CollaboratorEntry) => {
    setCollaborators((prev) =>
      prev.filter((c) =>
        collab.band_id != null
          ? c.band_id !== collab.band_id
          : c.pending_band_email !== collab.pending_band_email,
      ),
    )
  }

  // Debug: Monitor eventPerformances state changes
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="grid grid-cols-12 mb-10">
        <div className={"lg:col-span-7 col-span-12"}>
          <EventFlyerPhotoUploadSection
            setOriginalFile={setOriginalFile}
            setCropper={setCropper.bind(this)}
            setGifFile={handleGifUpload.bind(this)}
            gifPreview={gifPreview}
            coverImageUrl={watch("coverImageUrl")}
            onRemoveCoverImage={() => setRemoveCoverImage(true)}
            isGeneratedCover={isGeneratedCover}
            setIsProcessingPhoto={setIsProcessingPhoto}
          />
        </div>

        <div className="lg:col-span-5 col-span-12 lg:pl-4">
          <form
            onSubmit={handleSubmit(onSubmit, (errors) => {
              // Display validation errors from Yup schema
              if (errors && Object.keys(errors).length > 0) {
                const errorMessages = Object.values(errors)
                  .map((error) => error.message)
                  .filter(Boolean)
                setFormError(errorMessages.join(". "))
              } else {
                // Fallback validation for bandNada tickets if Yup validation isn't working
                const currentTicketType = watch("ticketType")
                if (currentTicketType === TICKET_TYPES.BN_TICKETS.value) {
                  const ticketPrice = watch("bandNadaTicketPrice")
                  const ticketMax = watch("bandNadaTicketMax")
                  const missingFields = []

                  if (!ticketPrice && ticketPrice !== 0) {
                    missingFields.push(
                      "Amount is required for bandNada tickets",
                    )
                  }
                  if (!ticketMax && ticketMax !== 0) {
                    missingFields.push(
                      "Amount is required for bandNada tickets",
                    )
                  }

                  if (missingFields.length > 0) {
                    setFormError(missingFields.join(". "))
                  }
                }
              }
            })}
          >
            {possibleDuplicate && possibleDuplicate.id !== dismissedDuplicateId && (
              <div className={formFieldContainerClasses}>
                <DuplicateEventWarning
                  duplicate={possibleDuplicate}
                  formattedDate={formatEventDate(possibleDuplicate.published_at)}
                  onDismiss={() => setDismissedDuplicateId(possibleDuplicate.id)}
                />
              </div>
            )}

            {!isEdit && bandId && (
              <div className={formFieldContainerClasses}>
                <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                  Duplicate Past Event
                </label>
                {isLoadingPastEvents ? (
                  <div className="flex items-center gap-2 py-2">
                    <Spinner className="text-gray-500" />
                    <span className="text-sm text-gray-600">
                      Loading past events…
                    </span>
                  </div>
                ) : (
                  <FormControl size="small" sx={muiInputStyles}>
                    <Select
                      value={selectedDuplicateEventId}
                      onChange={(e) => {
                        const val = e.target.value
                        setSelectedDuplicateEventId(val)
                        if (val) handleDuplicateFromPastEvent(val)
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>— Select to duplicate —</em>
                      </MenuItem>
                      {pastEvents.map((event: any) => {
                        const dateStr = formatEventDate(
                          event.active_date ||
                            event.start_dt ||
                            event.published_at,
                        )
                        const title = event.title || "Untitled event"
                        return (
                          <MenuItem key={event.id} value={event.id}>
                            {dateStr ? `${dateStr} – ${title}` : title}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                )}
              </div>
            )}
            <div className={formFieldContainerClasses}>
              <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                Event Name
              </label>
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    placeholder="Name your event"
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    sx={muiInputStyles}
                  />
                )}
              />
            </div>
            <div className={formFieldContainerClasses}>
              <label className="block text-gray-900 text-xs uppercase font-bold">
                Starts At
              </label>
              <Controller
                name="startDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <DateTimePicker
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) =>
                        field.onChange(date?.toDate() || null)
                      }
                      format="MMMM D, YYYY h:mm A"
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!error,
                          helperText: error?.message,
                          sx: muiInputStyles,
                        },
                      }}
                    />
                    {field.value && new Date(field.value) < new Date() && (
                      <p className="text-xs text-yellow-600 mt-1">
                        This event is in the past.
                      </p>
                    )}
                  </>
                )}
              />
            </div>
            <div className={formFieldContainerClasses}>
              <label className="block text-gray-900 text-xs uppercase font-bold">
                {useExplicitEndTime ? "Ends At" : "Duration"}
              </label>
              {/* Both fields stay mounted at all times (visibility toggled via CSS,
                  not conditional JSX) so react-hook-form's Controller for each one
                  never unmounts/remounts — that remount was the source of the
                  "resets to an empty/epoch value on toggle" bug. */}
              <div className={useExplicitEndTime ? "block" : "hidden"}>
                <Controller
                  name="endsAt"
                  control={control}
                  render={({ field }) => (
                    <>
                      <DateTimePicker
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date?.toDate() || null)
                        }
                        format="MMMM D, YYYY h:mm A"
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: muiInputStyles,
                          },
                        }}
                      />
                      {field.value &&
                        watchedStartDate &&
                        new Date(field.value) <= new Date(watchedStartDate) && (
                          <p className="text-xs text-yellow-600 mt-1">
                            End time must be after the start time.
                          </p>
                        )}
                    </>
                  )}
                />
              </div>
              <div className={useExplicitEndTime ? "hidden" : "block"}>
                <Controller
                  name="durationMinutes"
                  control={control}
                  render={({ field }) =>
                    durationIsCustom ? (
                      <TextField
                        size="small"
                        type="number"
                        sx={muiInputStyles}
                        inputProps={{ min: 1, step: 1 }}
                        InputProps={{
                          endAdornment: (
                            <span className="text-gray-500 text-sm whitespace-nowrap">
                              minutes
                            </span>
                          ),
                        }}
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? null
                              : parseInt(e.target.value, 10),
                          )
                        }
                      />
                    ) : (
                      <FormControl size="small" sx={muiInputStyles}>
                        <Select
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              parseInt(e.target.value as unknown as string),
                            )
                          }
                          displayEmpty
                        >
                          {eventDurationOptions.map((option) => (
                            <MenuItem key={option[0]} value={option[0]}>
                              {option[1]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )
                  }
                />
              </div>
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 mt-1 focus:outline-none"
                onClick={
                  useExplicitEndTime
                    ? switchToDuration
                    : switchToExplicitEndTime
                }
              >
                {useExplicitEndTime
                  ? "Use a duration instead"
                  : "Set end time explicitly"}
              </button>
            </div>
            <div className={formFieldContainerClasses}>
              <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                Event Location / Venue
              </label>
              <Controller
                name="eventLocation"
                control={control}
                render={({ field }) => (
                  <LocationSearchInput
                    // @ts-ignore
                    initLocation={field.value}
                    setLocation={field.onChange}
                    includeVenues={true}
                    className="col-span-11 appearance-none border border-mui-border-default rounded-md w-full text-base py-2 px-3 leading-tight focus:outline-none focus:border-2 focus:border-mui-border-focus hover:border-mui-border-hover transition-colors duration-200 text-mui-text-primary"
                  />
                )}
              />
            </div>
            <div className={formFieldContainerClasses}>
              <label className="block text-gray-900 text-xs uppercase font-bold">
                <span className="inline-flex items-center gap-2">
                  Ticket Type
                  <InfoTooltip
                    className="mb-1"
                    text={
                      <div>
                        <p className="leading-tight pb-1">
                          <strong>No Advance Tickets</strong> &mdash; RSVP
                          only, unlimited, no online payment or external
                          link.
                        </p>
                        <p className="leading-tight pb-1">
                          <strong>bandNada Tickets</strong> &mdash; sell
                          limited tickets through bandNada - you define
                          limit.
                        </p>
                        <p className="leading-tight pb-1">
                          <strong>External Tickets</strong> &mdash; link out
                          to another ticketing site.
                        </p>
                      </div>
                    }
                  />
                </span>
              </label>
              <Controller
                name="ticketType"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" sx={muiInputStyles}>
                    <Select
                      {...field}
                      onChange={(e) => field.onChange(e.target.value)}
                      displayEmpty
                    >
                      {ticketTypeOptions.map((option) => (
                        <MenuItem key={option[0]} value={option[0]}>
                          {option[1]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </div>
            {watch("ticketType") === TICKET_TYPES.BN_TICKETS.value && (
              <div className={formFieldContainerClasses}>
                <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                  <span className="inline-flex items-center gap-2">
                    Pricing Type
                    <InfoTooltip
                      className="mb-1"
                      text={
                        <div>
                          <p className="leading-tight pb-1 italic">
                            bandNada Price Type Options
                          </p>
                          <p className="leading-tight pb-1">
                            <strong>Free</strong> &mdash; no charge,
                            limited tickets, allows a donation option if
                            you want.
                          </p>
                          <p className="leading-tight pb-1">
                            <strong>Set Price</strong> &mdash; fixed price,
                            that can change on your defined schedule.
                          </p>
                          <p className="leading-tight pb-1">
                            <strong>Sliding Scale</strong> &mdash; buyer
                            picks price within your given range.
                          </p>
                        </div>
                      }
                    />
                  </span>
                </label>
                <Controller
                  name="pricingType"
                  control={control}
                  render={({ field }) => (
                    <FormControl size="small" sx={muiInputStyles}>
                      <Select
                        {...field}
                        value={field.value || PRICING_TYPES.SET_PRICE.value}
                      >
                        {pricingTypeOptionsFor(
                          TICKET_TYPES.BN_TICKETS.value,
                        ).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </div>
            )}
            {watch("ticketType") === TICKET_TYPES.BN_TICKETS.value &&
              watch("pricingType") === PRICING_TYPES.FREE.value && (
                <div className={formFieldContainerClasses}>
                  <Controller
                    name="acceptsDonation"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="flex items-center cursor-pointer text-sm">
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            color="primary"
                            style={{ padding: "0px 5px 0px 0px" }}
                          />
                          Include Donation
                        </label>
                        <a
                          href={`${for_venues_path()}#donations`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          see fee details
                        </a>
                      </div>
                    )}
                  />
                  {watch("acceptsDonation") && (
                    <Controller
                      name="donationText"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          multiline
                          minRows={2}
                          placeholder="Explain where the donation goes (optional)"
                          value={field.value || ""}
                          size="small"
                          fullWidth
                          sx={muiInputStyles}
                        />
                      )}
                    />
                  )}
                </div>
              )}
            {watch("ticketType") === TICKET_TYPES.BN_TICKETS.value &&
              watch("pricingType") !== PRICING_TYPES.FREE.value && (
                <div className={formFieldContainerClasses}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="flex items-center cursor-pointer text-sm">
                      <Checkbox
                        checked={watch("feeType") === FEE_TYPES.PRE_PURCHASE.value}
                        onChange={(e) =>
                          setValue(
                            "feeType",
                            e.target.checked
                              ? FEE_TYPES.PRE_PURCHASE.value
                              : FEE_TYPES.POST_PURCHASE.value,
                          )
                        }
                        color="primary"
                        style={{ padding: "0px 5px 0px 0px" }}
                      />
                      Add bandNada fee to purchase price
                    </label>
                  </div>
                </div>
              )}
            {watch("ticketType") === TICKET_TYPES.BN_TICKETS.value &&
              watch("pricingType") !== PRICING_TYPES.FREE.value && (
                <div className={formFieldContainerClasses}>
                  {watch("pricingType") ===
                  PRICING_TYPES.SLIDING_SCALE.value ? (
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-gray-900 text-xs uppercase font-bold">
                          {watch("feeType") === FEE_TYPES.PRE_PURCHASE.value
                            ? "Minimum Amount You'll Receive"
                            : "Minimum Ticket Price"}
                        </label>
                        <Controller
                          name="bandNadaTicketPrice"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              type="number"
                              placeholder="5.00"
                              value={field.value?.toString() || ""}
                              onChange={(e) => {
                                const value = e.target.value
                                if (
                                  value === "" ||
                                  /^\d*\.?\d{0,2}$/.test(value)
                                ) {
                                  field.onChange(
                                    value === "" ? null : parseFloat(value),
                                  )
                                }
                              }}
                              size="small"
                              error={!!error}
                              helperText={error?.message}
                              InputProps={{
                                startAdornment: (
                                  <span style={{ marginRight: 8 }}>$</span>
                                ),
                                inputProps: { step: "0.01", min: "0" },
                              }}
                              sx={muiInputStyles}
                            />
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-gray-900 text-xs uppercase font-bold">
                          {watch("feeType") === FEE_TYPES.PRE_PURCHASE.value
                            ? "Maximum Amount You'll Receive"
                            : "Maximum Ticket Price"}
                        </label>
                        <Controller
                          name="bandNadaTicketPriceMax"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              type="number"
                              placeholder="25.00"
                              value={field.value?.toString() || ""}
                              onChange={(e) => {
                                const value = e.target.value
                                if (
                                  value === "" ||
                                  /^\d*\.?\d{0,2}$/.test(value)
                                ) {
                                  field.onChange(
                                    value === "" ? null : parseFloat(value),
                                  )
                                }
                              }}
                              size="small"
                              error={!!error}
                              helperText={error?.message}
                              InputProps={{
                                startAdornment: (
                                  <span style={{ marginRight: 8 }}>$</span>
                                ),
                                inputProps: { step: "0.01", min: "0" },
                              }}
                              sx={muiInputStyles}
                            />
                          )}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                        {watch("feeType") === FEE_TYPES.PRE_PURCHASE.value
                          ? "Amount You'll Receive"
                          : "Ticket Price"}
                      </label>
                      <Controller
                        name="bandNadaTicketPrice"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            type="number"
                            placeholder="5.00"
                            value={field.value?.toString() || ""}
                            onChange={(e) => {
                              const value = e.target.value
                              if (
                                value === "" ||
                                /^\d*\.?\d{0,2}$/.test(value)
                              ) {
                                field.onChange(
                                  value === "" ? null : parseFloat(value),
                                )
                              }
                            }}
                            size="small"
                            error={!!error}
                            helperText={error?.message}
                            InputProps={{
                              startAdornment: (
                                <span style={{ marginRight: 8 }}>$</span>
                              ),
                              inputProps: { step: "0.01", min: "0" },
                            }}
                            sx={muiInputStyles}
                          />
                        )}
                      />
                      {(() => {
                        const price = watch("bandNadaTicketPrice")
                        if (typeof price !== "number" || price <= 0) {
                          return null
                        }
                        const isPrePurchase =
                          watch("feeType") === FEE_TYPES.PRE_PURCHASE.value
                        return (
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-gray-500">
                              {isPrePurchase ? (
                                <>
                                  Buyers will pay{" "}
                                  {formatPrice(
                                    calculateGrossPriceForBandShare(price),
                                  )}{" "}
                                  so you receive {formatPrice(price)}.
                                </>
                              ) : (
                                <>
                                  You&apos;ll receive{" "}
                                  {formatPrice(
                                    calculateBandShareForTicketPrice(price),
                                  )}{" "}
                                  of this {formatPrice(price)} ticket.
                                </>
                              )}
                            </span>
                            <a
                              href={`${for_venues_path()}#donations`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 underline"
                            >
                              see fee details
                            </a>
                          </div>
                        )
                      })()}
                    </>
                  )}
                  {watch("pricingType") === PRICING_TYPES.SET_PRICE.value &&
                    ((pricingSchedule.length === 0 && (
                      <button
                        type="button"
                        className="text-sm text-blue-600 hover:text-blue-800 mt-1"
                        onClick={() => {
                          const eventDate = watch("startDate")
                          const currentPrice = watch("bandNadaTicketPrice")
                          const id = pricingScheduleIdCounter
                          setPricingScheduleIdCounter((c) => c + 1)
                          setPricingSchedule([
                            {
                              id,
                              price_changes_at:
                                eventDate instanceof Date
                                  ? eventDate.toISOString()
                                  : eventDate
                                    ? new Date(eventDate).toISOString()
                                    : "",
                              price_changes_to:
                                typeof currentPrice === "number"
                                  ? currentPrice
                                  : 0,
                            },
                          ])
                        }}
                      >
                        Add Pricing Schedule
                      </button>
                    )) ||
                      (pricingSchedule.length > 0 && (
                        <div className="mt-3 p-3 border border-gray-200 rounded bg-gray-50 space-y-3">
                          {pricingSchedule.map((row, index) => (
                            <div
                              key={row.id ?? index}
                              className="flex flex-nowrap items-end gap-3"
                            >
                              <div className="min-w-0 flex-[2]">
                                <label className="block text-gray-900 text-xs uppercase font-bold">
                                  Price Changes At
                                </label>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <DateTimePicker
                                    value={
                                      row.price_changes_at
                                        ? dayjs(row.price_changes_at)
                                        : null
                                    }
                                    onChange={(value) => {
                                      setPricingSchedule((prev) => {
                                        const next = [...prev]
                                        next[index] = {
                                          ...next[index],
                                          price_changes_at: value
                                            ? value.toISOString()
                                            : "",
                                        }
                                        return next
                                      })
                                    }}
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        fullWidth: true,
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </div>
                              <div className="flex-1" style={{ minWidth: 75 }}>
                                <label className="block text-gray-900 text-xs uppercase font-bold">
                                  {watch("feeType") === FEE_TYPES.PRE_PURCHASE.value
                                    ? "Amount you'll receive"
                                    : "Price changes to"}
                                </label>
                                <TextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={row.price_changes_to}
                                  onChange={(e) => {
                                    const v = e.target.value
                                    const num = v === "" ? 0 : parseFloat(v)
                                    if (!Number.isNaN(num)) {
                                      setPricingSchedule((prev) => {
                                        const next = [...prev]
                                        next[index] = {
                                          ...next[index],
                                          price_changes_to: num,
                                        }
                                        return next
                                      })
                                    }
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <span style={{ marginRight: 8 }}>$</span>
                                    ),
                                    inputProps: { min: 0, step: "0.01" },
                                  }}
                                />
                              </div>
                              <div className="flex-shrink-0 pb-1">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPricingSchedule((prev) =>
                                      prev.filter((_, i) => i !== index),
                                    )
                                  }
                                  className="p-1 rounded transition-colors focus:outline-none text-red-600 hover:text-red-800 hover:bg-red-50"
                                  title="Remove price change"
                                >
                                  <Trash className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="text-sm text-blue-600 hover:text-blue-800"
                            onClick={() => {
                              const eventDate = watch("startDate")
                              const currentPrice = watch("bandNadaTicketPrice")
                              const id = pricingScheduleIdCounter
                              setPricingScheduleIdCounter((c) => c + 1)
                              setPricingSchedule((prev) => [
                                ...prev,
                                {
                                  id,
                                  price_changes_at:
                                    eventDate instanceof Date
                                      ? eventDate.toISOString()
                                      : eventDate
                                        ? new Date(eventDate).toISOString()
                                        : "",
                                  price_changes_to:
                                    typeof currentPrice === "number"
                                      ? currentPrice
                                      : 0,
                                },
                              ])
                            }}
                          >
                            Add another price change
                          </button>
                        </div>
                      )))}
                </div>
              )}
            {watch("ticketType") === TICKET_TYPES.BN_TICKETS.value && (
              <div className={formFieldContainerClasses}>
                <label className="block text-gray-900 text-xs uppercase font-bold">
                  Max Tickets
                </label>
                <Controller
                  name="bandNadaTicketMax"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      placeholder="Cap the number of tickets that can be sold"
                      value={field.value?.toString() || ""}
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        inputProps: {
                          min: "1",
                          step: "1",
                        },
                      }}
                      sx={muiInputStyles}
                    />
                  )}
                />
              </div>
            )}
            {watch("ticketType") === TICKET_TYPES.BN_TICKETS.value &&
              watch("pricingType") !== PRICING_TYPES.FREE.value && (
                <div className={formFieldContainerClasses}>
                  <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                    Ticket Price at Door (Optional)
                  </label>
                  <Controller
                    name="ticketPriceAtDoor"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type="number"
                        placeholder="Informational only"
                        value={field.value?.toString() || ""}
                        onChange={(e) => {
                          const value = e.target.value
                          // Allow empty string or valid numbers
                          if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                            field.onChange(
                              value === "" ? null : parseFloat(value),
                            )
                          }
                        }}
                        size="small"
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          startAdornment: (
                            <span style={{ marginRight: 8 }}>$</span>
                          ),
                          inputProps: {
                            step: "0.01",
                            min: "0",
                          },
                        }}
                        sx={muiInputStyles}
                      />
                    )}
                  />
                </div>
              )}
            {watch("ticketType") === TICKET_TYPES.EXTERNAL_TICKETS.value && (
              <div className={formFieldContainerClasses}>
                <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                  External Ticket Link
                </label>
                <Controller
                  name="eventTicketLinkUrl"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="text"
                      placeholder="External Ticket URL"
                      value={field.value?.toString() || ""}
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                      sx={muiInputStyles}
                    />
                  )}
                />
              </div>
            )}
            {(watch("ticketType") === TICKET_TYPES.NO_ADVANCE_TICKETS.value ||
              watch("ticketType") === TICKET_TYPES.EXTERNAL_TICKETS.value) && (
              <div className={formFieldContainerClasses}>
                <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                  <span className="inline-flex items-center gap-2">
                    Pricing Type
                    <InfoTooltip
                      className="mb-1"
                      text={
                        watch("ticketType") ===
                        TICKET_TYPES.EXTERNAL_TICKETS.value ? (
                          <div>
                            <p className="leading-tight pb-1 italic">
                              External Ticket Pricing is Informational
                            </p>
                            <p className="leading-tight pb-1">
                              <strong>Free</strong> &mdash; no charge
                              online, or at door.
                            </p>
                            <p className="leading-tight pb-1">
                              <strong>Set Price</strong> &mdash; one fixed
                              price.
                            </p>
                            <p className="leading-tight pb-1">
                              <strong>Sliding Scale</strong> &mdash; buyer
                              picks price in given range.
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="leading-tight pb-1 italic">
                              No Advance Ticket Pricing is Informational
                            </p>
                            <p className="leading-tight pb-1">
                              <strong>Free</strong> &mdash; no charge
                              online, or at door, but allows a donation
                              option.
                            </p>
                            <p className="leading-tight pb-1">
                              <strong>Set Price</strong> &mdash; fixed
                              price at door.
                            </p>
                            <p className="leading-tight pb-1">
                              <strong>Sliding Scale</strong> &mdash; pay
                              what you want at door.
                            </p>
                          </div>
                        )
                      }
                    />
                  </span>
                </label>
                <Controller
                  name="pricingType"
                  control={control}
                  render={({ field }) => (
                    <FormControl size="small" sx={muiInputStyles}>
                      <Select
                        {...field}
                        value={field.value || PRICING_TYPES.FREE.value}
                      >
                        {pricingTypeOptionsFor(watch("ticketType")).map(
                          ([value, label]) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  )}
                />
              </div>
            )}
            {(watch("ticketType") === TICKET_TYPES.NO_ADVANCE_TICKETS.value ||
              watch("ticketType") === TICKET_TYPES.EXTERNAL_TICKETS.value) &&
              watch("pricingType") === PRICING_TYPES.FREE.value && (
                <div className={formFieldContainerClasses}>
                  <Controller
                    name="acceptsDonation"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="flex items-center cursor-pointer text-sm">
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            color="primary"
                            style={{ padding: "0px 5px 0px 0px" }}
                          />
                          Include Donation
                        </label>
                        <a
                          href={`${for_venues_path()}#donations`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          see fee details
                        </a>
                      </div>
                    )}
                  />
                  {watch("acceptsDonation") && (
                    <Controller
                      name="donationText"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          multiline
                          minRows={2}
                          placeholder="Explain where the donation goes (optional)"
                          value={field.value || ""}
                          size="small"
                          fullWidth
                          sx={muiInputStyles}
                        />
                      )}
                    />
                  )}
                </div>
              )}
            {(watch("ticketType") === TICKET_TYPES.NO_ADVANCE_TICKETS.value ||
              watch("ticketType") === TICKET_TYPES.EXTERNAL_TICKETS.value) &&
              watch("pricingType") !== PRICING_TYPES.FREE.value && (
                <div className={formFieldContainerClasses}>
                  {watch("pricingType") ===
                  PRICING_TYPES.SLIDING_SCALE.value ? (
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-gray-900 text-xs uppercase font-bold">
                          Minimum Ticket Price
                        </label>
                        <Controller
                          name="ticketPrice"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              type="number"
                              placeholder="0.00"
                              value={field.value?.toString() || ""}
                              onChange={(e) => {
                                const value = e.target.value
                                if (
                                  value === "" ||
                                  /^\d*\.?\d{0,2}$/.test(value)
                                ) {
                                  field.onChange(
                                    value === "" ? null : parseFloat(value),
                                  )
                                }
                              }}
                              size="small"
                              error={!!error}
                              helperText={error?.message}
                              InputProps={{
                                startAdornment: (
                                  <span style={{ marginRight: 8 }}>$</span>
                                ),
                                inputProps: { step: "0.01", min: "0" },
                              }}
                              sx={muiInputStyles}
                            />
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-gray-900 text-xs uppercase font-bold">
                          Maximum Ticket Price
                        </label>
                        <Controller
                          name="ticketPriceMax"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              type="number"
                              placeholder="0.00"
                              value={field.value?.toString() || ""}
                              onChange={(e) => {
                                const value = e.target.value
                                if (
                                  value === "" ||
                                  /^\d*\.?\d{0,2}$/.test(value)
                                ) {
                                  field.onChange(
                                    value === "" ? null : parseFloat(value),
                                  )
                                }
                              }}
                              size="small"
                              error={!!error}
                              helperText={error?.message}
                              InputProps={{
                                startAdornment: (
                                  <span style={{ marginRight: 8 }}>$</span>
                                ),
                                inputProps: { step: "0.01", min: "0" },
                              }}
                              sx={muiInputStyles}
                            />
                          )}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                        Ticket Price
                      </label>
                      <Controller
                        name="ticketPrice"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            type="number"
                            placeholder="0.00"
                            value={field.value?.toString() || ""}
                            onChange={(e) => {
                              const value = e.target.value
                              if (
                                value === "" ||
                                /^\d*\.?\d{0,2}$/.test(value)
                              ) {
                                field.onChange(
                                  value === "" ? null : parseFloat(value),
                                )
                              }
                            }}
                            size="small"
                            error={!!error}
                            helperText={error?.message}
                            InputProps={{
                              startAdornment: (
                                <span style={{ marginRight: 8 }}>$</span>
                              ),
                              inputProps: { step: "0.01", min: "0" },
                            }}
                            sx={muiInputStyles}
                          />
                        )}
                      />
                    </>
                  )}
                </div>
              )}

            {/* Event Performances Section */}
            <div className={formFieldContainerClasses}>
              <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                Lineup
              </label>
              <div className="col-span-11 space-y-4">
                {(() => {
                  const activePerformances = eventPerformances.filter(
                    (p) => !p._destroy,
                  )
                  const isDraggable =
                    !editingPerformance &&
                    !activePerformances.some((p) => p.set_time)
                  return (
                    <DragDropContext onDragEnd={reorderPerformances}>
                      <Droppable
                        droppableId="lineup"
                        isDropDisabled={!isDraggable}
                      >
                        {(droppableProvided) => (
                          <div
                            ref={droppableProvided.innerRef}
                            {...droppableProvided.droppableProps}
                          >
                            {activePerformances.map((performance, index) =>
                              editingPerformance?.performance_order ===
                              performance.performance_order ? (
                                <Draggable
                                  key={`${performance.performance_order}`}
                                  draggableId={`${performance.performance_order}`}
                                  index={index}
                                  isDragDisabled={true}
                                >
                                  {(draggableProvided) => (
                                    <div
                                      ref={draggableProvided.innerRef}
                                      {...draggableProvided.draggableProps}
                                      className="bg-yellow-100 my-4 p-2 shadow-md transition-all duration-500"
                                    >
                                      <EventPerformance
                                        performance={performance}
                                        index={index}
                                        onRemove={removeEventPerformance}
                                        onEdit={editEventPerformance}
                                        isEditing={true}
                                        isAnyEditing={true}
                                      />
                                      <EventPerformanceForm
                                        onAddToLineup={addBandToLineup}
                                        onUpdatePerformance={
                                          updateEventPerformance
                                        }
                                        onCancel={cancelEdit}
                                        index={currentLineupIndex}
                                        eventStartDate={
                                          watch("startDate") || null
                                        }
                                        existingPerformance={editingPerformance}
                                        isEditing={true}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              ) : (
                                <Draggable
                                  key={`${performance.performance_order}`}
                                  draggableId={`${performance.performance_order}`}
                                  index={index}
                                  isDragDisabled={!isDraggable}
                                >
                                  {(draggableProvided) => (
                                    <div
                                      ref={draggableProvided.innerRef}
                                      {...draggableProvided.draggableProps}
                                    >
                                      <EventPerformance
                                        performance={performance}
                                        index={index}
                                        onRemove={removeEventPerformance}
                                        onEdit={editEventPerformance}
                                        isEditing={false}
                                        isAnyEditing={!!editingPerformance}
                                        dragHandleProps={
                                          draggableProvided.dragHandleProps
                                        }
                                        isDraggable={isDraggable}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              ),
                            )}
                            {droppableProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )
                })()}
                {!editingPerformance && (
                  <EventPerformanceForm
                    onAddToLineup={addBandToLineup}
                    index={currentLineupIndex}
                    eventStartDate={watch("startDate") || null}
                  />
                )}
              </div>
            </div>

            <div className={formFieldContainerClasses}>
              <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
                Description
              </label>
              <Controller
                name="eventDescription"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <TaggableInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="What are the details of the event"
                      className="event-form"
                    />
                    {error && (
                      <span className="text-red-500 text-sm">
                        {error.message}
                      </span>
                    )}
                  </>
                )}
              />
              <div className="text-xs text-gray-500 leading-tight mt-1">
                <span role="img" aria-label="hot tip">
                  🔥
                </span>{" "}
                No need to list the bands playing in the Description when using
                our Lineup functionality above
                <span role="img" aria-label="hot tip">
                  🔥
                </span>
              </div>
            </div>
            {isEdit ? (
              <NotifyAttendeesCheckbox
                checked={sendNotifications}
                onChange={setSendNotifications}
                ticketsCount={Number(
                  (concert as any).band_nada_tickets_sold ?? 0,
                )}
                rsvpsCount={Number(
                  (concert as any).event_rsvp_count_excluding_owner ?? 0,
                )}
              />
            ) : (
              <div className={formFieldContainerClasses}>
                {user?.band?.band_type === "venue" ? (
                  <div className="text-xs text-gray-500">
                    {!isLoadingSceneCount &&
                    sceneCount !== undefined &&
                    sceneLabel
                      ? `${sceneCount} people will see it in the ${sceneLabel} feed and digest`
                      : "Everyone in your scene will see this in their feed and digest"}
                  </div>
                ) : (
                  <>
                    <Checkbox
                      checked={sendNotifications}
                      onChange={(e) => setSendNotifications(e.target.checked)}
                      color="primary"
                      style={{
                        padding: "0px 5px 0px 0px",
                        verticalAlign: "top",
                      }}
                    />
                    <label>Notify Followers?</label>
                    <div className="text-xs text-gray-500">
                      {!isLoadingFollowerCount && notifiersCount !== undefined
                        ? `${notifiersCount} ${notifiersCount === 1 ? "person" : "people"} will ${sendNotifications ? "be" : "NOT be"} notified now`
                        : "People who follow you"}
                      {!isLoadingSceneCount &&
                      sceneCount !== undefined &&
                      sceneLabel
                        ? ` - (${sceneCount} people will see it in the ${sceneLabel} feed and digest regardless)`
                        : ""}{" "}
                    </div>
                  </>
                )}
              </div>
            )}
            {(!isEdit || user?.band_id === concert.band_id) && (
              <CollaboratorsForm
                collaborators={collaborators}
                suggestions={collaboratorSuggestions}
                postId={concert.id}
                onAdd={addCollaborator}
                onRemove={removeCollaborator}
              />
            )}
            <>
              <button
                type="button"
                className="text-blue-600 italic text-sm mb-2 focus:outline-none"
                onClick={(e) => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? "Hide" : "Show"} Advanced
              </button>
              {showAdvanced && (
                <>
                  <div className={formFieldContainerClasses}>
                    <label className="block text-gray-900 text-xs uppercase font-bold">
                      Publish Status
                    </label>
                    <Controller
                      name="eventStatus"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small" sx={muiInputStyles}>
                          <Select {...field} displayEmpty>
                            {eventStatusOptions.map((option) => (
                              <MenuItem key={option[0]} value={option[0]}>
                                {option[1]}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>
                  {watch("eventStatus") !== "unpublished" && (
                    <div className={formFieldContainerClasses}>
                      <label className="block text-gray-900 text-xs uppercase font-bold">
                        Announce Date
                      </label>
                      <div>
                        <Controller
                          name="announcedAt"
                          control={control}
                          render={({ field }) => (
                            <DateTimePicker
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(date) =>
                                field.onChange(date?.toDate() || null)
                              }
                              format="MMMM D, YYYY h:mm A"
                              readOnly={watch("eventStatus") === "unpublished"}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  sx: muiInputStyles,
                                  placeholder:
                                    "Leave blank to announce immediately",
                                },
                                field: { clearable: true },
                              }}
                            />
                          )}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        hide from public until this date/time.
                      </p>
                    </div>
                  )}
                  <div className={formFieldContainerClasses}>
                    <label className="block text-gray-900 text-xs uppercase font-bold">
                      Timezone
                    </label>
                    <Controller
                      name="timezone"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small" sx={muiInputStyles}>
                          <Select {...field} displayEmpty>
                            {Intl.supportedValuesOf("timeZone").map((tz) => (
                              <MenuItem key={tz} value={tz}>
                                {tz}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>
                </>
              )}
            </>
            <div className="col-span-12 text-red-700 lg:text-sm text-lg">
              {formError}
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <LoadingButton
                  type="submit"
                  isLoading={isSubmitting || isUploading || isProcessingPhoto}
                  loadingText={isEdit ? "Updating..." : "Creating..."}
                  className="mb-1"
                  bg="bg-green-700"
                >
                  {isEdit ? "Update Event" : "Create Event"}
                </LoadingButton>
                {isEdit &&
                  concert.band_id === user?.band?.id &&
                  (() => {
                    const ticketsCount = Number(
                      (concert as any).band_nada_tickets_sold ?? 0,
                    )
                    const rsvpsCount = Number(
                      (concert as any).event_rsvp_count_excluding_owner ?? 0,
                    )
                    const hasAttendees = ticketsCount > 0 || rsvpsCount > 0
                    return hasAttendees ? (
                      <>
                        <Button
                          bg="bg-red-500 mb-1"
                          onClick={() => setShowCancelModal(true)}
                          disabled={isSubmitting}
                        >
                          Cancel Event
                        </Button>
                        <EventCancelModal
                          isOpen={showCancelModal}
                          onClose={() => setShowCancelModal(false)}
                          onConfirm={async (
                            canceled_event_message,
                            notify_attendees,
                          ) => {
                            await cancelEventPost(
                              concert.id!,
                              concert.band_id!,
                              {
                                canceled_event_message,
                                notify_attendees,
                              },
                            )
                            queryClient.invalidateQueries({
                              queryKey: ["posts"],
                            })
                            setShowForm?.(null)
                          }}
                          ticketsCount={ticketsCount}
                          rsvpsCount={rsvpsCount}
                          hasBandNadaTickets={
                            concert.ticket_type ===
                            TICKET_TYPES.BN_TICKETS.value
                          }
                        />
                      </>
                    ) : (
                      <PostDeleteButton
                        postId={concert.id}
                        bandId={concert.band_id}
                        setShowForm={setShowForm}
                        queryKey={["posts"]}
                      />
                    )
                  })()}
              </div>
              <Button
                disabled={isSubmitting}
                bg="bg-gray-400"
                onClick={() =>
                  setShowForm
                    ? setShowForm(false)
                    : (window.location.href = root_path())
                }
                style={{ height: "fit-content" }}
                className="mb-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </LocalizationProvider>
  )
}

export default EventForm
